import React, {useLayoutEffect} from 'react';
import styled from 'styled-components/native';
import {Platform, StatusBar} from 'react-native';
import {hdp} from '../../utils/responsive';
import {KeyboardAwareScrollView} from 'react-native-keyboard-aware-scroll-view';
import {MainLayoutProps} from '../../interfaces/screens/mainLayout';
import {NativeStackHeaderProps} from '@react-navigation/native-stack';
import {NavigationProp, useNavigation} from '@react-navigation/native';
import Header from '../../components/header';
import Animated, {FadeIn, FadeOut} from 'react-native-reanimated';
import {
  useSafeAreaFrame,
  useSafeAreaInsets,
} from 'react-native-safe-area-context';
import {getDefaultHeaderHeight} from '@react-navigation/elements';
import {useSelector} from 'react-redux';
import {RootState} from '../../redux/store';

const Container = styled.View<{backgroundColor?: string}>`
  width: 100%;
  height: 100%;
  background-color: ${({theme, backgroundColor}) =>
    backgroundColor || theme.homeBackground};
`;
/**
 * Styled component for the main layout content container.
 */
const ContentContainer = styled.View<{
  keyboardAvoidScrollView?: boolean;
  noPadding?: boolean;
  headerHeight?: number;
}>`
  padding-horizontal: ${({keyboardAvoidScrollView, noPadding}) =>
    keyboardAvoidScrollView || noPadding ? 0 : 5}%;
  flex-grow: 1;
  padding-top: ${({headerHeight}) => headerHeight || 0}px;
`;

/**
 * Styled component for the main view.
 */
const MainView = styled.View`
  flex-grow: 1;
  height: 100%;
`;

/**
 * Styled component for the scroll view.
 */
const ScrollView = styled(Animated.ScrollView)``;

const Overlay = styled(Animated.View)<{overlayColor?: string}>`
  position: absolute;
  width: 100%;
  height: 100%;
  background-color: ${({theme, overlayColor}) =>
    overlayColor || theme.backgroundOverlay};
`;

/**
 * Main layout component.
 *
 * @param children
 * @param backgroundColor
 * @param isDarkMode
 * @param extraScrollHeight
 * @param menuHeader
 * @param backHeader
 * @param showOverlay
 * @param overlayColor
 * @param noPaddingHeader
 * @param isCustomHeader
 * @param {MainLayoutProps} props - The main layout component props.
 * @param {boolean} [props.enableScroll=true] - Enable or disable scrolling.
 * @param {boolean} [props.noPadding=false] - Disable padding for the main content.
 * @param {boolean} [props.keyboardAvoidScrollView] - Enable keyboard-aware scroll view.
 * @param {boolean} [props.enableOnAndroid] - Enable keyboard-aware scroll view on Android.
 * @returns {React.ReactElement} The rendered main layout component.
 */
const MainLayout = ({
  children,
  enableScroll = true,
  noPadding = false,
  keyboardAvoidScrollView,
  enableOnAndroid,
  backgroundColor,
  isDarkMode,
  extraScrollHeight,
  menuHeader,
  backHeader,
  showOverlay = true,
  overlayColor,
  noPaddingHeader,
  ...props
}: MainLayoutProps): React.ReactElement => {
  const navigation = useNavigation<NavigationProp<any>>();
  const {darkMode} = useSelector((state: RootState) => state?.settings) || {};
  const {top: topInset} = useSafeAreaInsets();
  const frame = useSafeAreaFrame();
  // Custom header height.
  const headerHeight = getDefaultHeaderHeight(frame, false, topInset);

  const PageView: any = enableScroll ? ScrollView : MainView;

  // Apply custom header via navigation options.
  useLayoutEffect(() => {
    if (menuHeader || backHeader) {
      const header = (
        headerProps: NativeStackHeaderProps,
      ): React.ReactElement => (
        <Header
          backHeader={backHeader}
          menuHeader={menuHeader}
          isDarkMode={isDarkMode}
          {...headerProps}
          {...props}
        />
      );
      navigation?.setOptions({header, headerShown: true});
    }
  }, [props, isDarkMode, backHeader, menuHeader]);

  return (
    <Container backgroundColor={backgroundColor}>
      <StatusBar
        key={String(darkMode)}
        animated
        translucent
        backgroundColor={'transparent'}
        barStyle={darkMode ? 'light-content' : 'dark-content'}
        showHideTransition={'fade'}
      />
      {showOverlay && (
        <Overlay
          overlayColor={overlayColor}
          entering={FadeIn}
          exiting={FadeOut}
        />
      )}
      <PageView
        automaticallyAdjustContentInsets
        automaticallyAdjustsScrollIndicatorInsets
        keyboardAvoidScrollView={keyboardAvoidScrollView}
        contentContainerStyle={{
          flexGrow: 1,
          paddingTop:
            noPaddingHeader || keyboardAvoidScrollView ? 0 : headerHeight,
        }}
        style={[
          noPadding && {paddingHorizontal: 0},
          {
            height: '100%',
            flexGrow: 1,
          },
        ]}>
        <ContentContainer
          headerHeight={
            keyboardAvoidScrollView || enableScroll || noPaddingHeader
              ? 0
              : headerHeight
          }
          noPadding={noPadding}
          keyboardAvoidScrollView={keyboardAvoidScrollView}>
          {keyboardAvoidScrollView ? (
            <KeyboardAwareScrollView
              bounces={false}
              extraScrollHeight={extraScrollHeight || hdp(5)}
              enableOnAndroid={enableOnAndroid}
              contentContainerStyle={{
                flexGrow: 1,
                paddingHorizontal: noPadding ? 0 : '5%',
                paddingTop: noPaddingHeader ? 0 : headerHeight,
              }}
              keyboardShouldPersistTaps="handled">
              {children}
            </KeyboardAwareScrollView>
          ) : (
            children
          )}
        </ContentContainer>
      </PageView>
    </Container>
  );
};
export default MainLayout;
